import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import Draggable from "react-draggable";
import { ReactSVG } from "react-svg";
import { toPng, toJpeg, toBlob, toPixelData, toSvg } from "html-to-image";
import download from "downloadjs";

import classes from "./card2.module.css";
import {
  boldAndItalicStyles,
  calculateCardGradient,
  calculateStarHeight,
  calculateTitleGradient,
  cardDimentionsAtScaleX,
  _description,
} from "../../handlers/helperFunctions";

const Card2 = ({
  mainBg,
  opacity,
  outerBorder,
  mainTitle,
  secondTitle,
  bottomTitle,
  description,
  bottomStrip,
  selectedSmIcons,
  cardBacksideImg,
  isFrontView,
  imageRef,
  backImageRef,
  generatingPreview,
}) => {
  const [btnShow, setBtnShow] = useState(false);
  const [mainTitleYAxisPosition, setMainTitleYAxisPosition] = useState(
    mainTitleYAxisBounds[0]
  );
  const [secondTitleYAxisPosition, setSecondTitleYAxisPosition] = useState();
  const [bottomTitleYAxisPosition, setBottomTitleYAxisPosition] = useState(
    BottomTitleYAxisBounds[1]
  );

  const ref = useRef(null);
  const handleClick = useCallback(async () => {
    if (ref.current) {
      setBtnShow(true);
      download(await toJpeg(ref.current), "test.jpg");
      download(await toSvg(ref.current), "test.svg");
      setBtnShow(false);
    }
  }, [ref?.current]);

  const outlineContainerStyles = {
    border: outerBorder?.isVisible && `1px solid ${outerBorder?.color}`,
    background: calculateCardGradient(
      outerBorder?.gradientColor1,
      outerBorder?.gradientColor2,
      outerBorder?.gradient1Spread,
      outerBorder?.gradient2Spread
    ),
  };

  const mainTitleContainerStyles = useMemo(
    () => ({
      background: calculateTitleGradient(
        mainTitle?.gradientColor1,
        mainTitle?.gradientColor2,
        mainTitle?.gradient1Spread,
        mainTitle?.gradient2Spread,
        mainTitle?.isFlat
      ),
      border: `1px solid ${mainTitle?.outline}`,
      borderLeft: 0,
      borderRight: 0,
      borderTop:
        mainTitleYAxisBounds[0] == mainTitleYAxisPosition
          ? 0
          : `1px solid ${mainTitle?.outline}`,
      borderBottom:
        mainTitleYAxisBounds[1] == mainTitleYAxisPosition
          ? 0
          : `1px solid ${mainTitle?.outline}`,
    }),
    [mainTitle, mainTitleYAxisPosition]
  );

  const secondTitleContainerStyles = useMemo(
    () => ({
      background: calculateTitleGradient(
        secondTitle?.gradientColor1,
        secondTitle?.gradientColor2,
        secondTitle?.gradient1Spread,
        secondTitle?.gradient2Spread,
        secondTitle?.isFlat
      ),
      border: `1px solid ${secondTitle?.outline}`,
      borderLeft: 0,
      borderRight: 0,
      borderTop:
        secondTitleYAxisBounds[0] == secondTitleYAxisPosition
          ? 0
          : `1px solid ${secondTitle?.outline}`,
      borderBottom:
        secondTitleYAxisBounds[1] == secondTitleYAxisPosition
          ? 0
          : `1px solid ${secondTitle?.outline}`,
    }),
    [secondTitle, secondTitleYAxisPosition]
  );

  const bottomTitleContainerStyles = useMemo(
    () => ({
      background: calculateTitleGradient(
        bottomTitle?.gradientColor1,
        bottomTitle?.gradientColor2,
        bottomTitle?.gradient1Spread,
        bottomTitle?.gradient2Spread,
        bottomTitle?.isFlat
      ),
      border: `1px solid ${bottomTitle?.outline}`,
      borderLeft: 0,
      borderRight: 0,
      borderTop:
        BottomTitleYAxisBounds[0] == bottomTitleYAxisPosition
          ? 0
          : `1px solid ${bottomTitle?.outline}`,
      borderBottom:
        BottomTitleYAxisBounds[1] == bottomTitleYAxisPosition
          ? 0
          : `1px solid ${bottomTitle?.outline}`,
    }),
    [bottomTitle, bottomTitleYAxisPosition]
  );

  return (
    <div>
      {/* {isFrontView && ( */}
      <div
        ref={imageRef}
        style={{
          ...cardDimentionsAtScaleX(2),
          height: generatingPreview ? "1080px" : "calc(100vh - 76px)",
        }}
        // className={[classes?.dflex, !isFrontView && classes?.hideContainer].join(" ")}

        className={`${classes?.dflex} ${
          !isFrontView && !generatingPreview && classes?.hideContainer
        }`}
      >
        <div
          className={classes?.container}
          style={{ scale: generatingPreview ? "4" : "2" }}
        >
          {/* Outline */}
          <div className={classes?.mainOutline} style={outlineContainerStyles}>
            {/* Card Main */}
            <div
              className={classes?.cardMain}
              style={{
                border: `1px solid ${outerBorder?.outline}`,
              }}
            >
              <Draggable bounds="parent">
                {/* Main Bg */}
                {mainBg?.type?.split("/")[1] == "svg+xml" ||
                (!mainBg?.type && mainBg?.split(".")[2] == "svg") ? (
                  <ReactSVG
                    src={typeof mainBg == "string" ? mainBg : mainBg}
                    className={classes?.mainBgImg}
                    draggable={false}
                    style={{ opacity }}
                  />
                ) : (
                  <img
                    src={mainBg}
                    className={classes?.mainBgImg}
                    style={{ opacity }}
                  />
                )}
              </Draggable>
              {/* First Title */}
              {mainTitle?.isVisible && (
                <Draggable
                  bounds="parent"
                  onStop={(_, { y }) => setMainTitleYAxisPosition(y)}
                >
                  <div
                    className={`${classes?.titleContainer} ${classes?.topTitlePosition} ${classes?.titleContainerWithIcons}`}
                    style={mainTitleContainerStyles}
                  >
                    <p
                      className={`maxLine1`}
                      style={{
                        color: mainTitle?.color,
                        ...boldAndItalicStyles(
                          mainTitle?.fontStyle,
                          mainTitle?.fontWeight
                        ),
                      }}
                    >
                      {mainTitle?.text}
                    </p>
                    {selectedSmIcons?.length > 0 && (
                      <div>
                        {selectedSmIcons?.map((icon, i) => {
                          return (
                            <img
                              src={icon}
                              key={i}
                              className={classes?.iconSm}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Draggable>
              )}
              {/* Middle Title */}
              {secondTitle?.isVisible && (
                <Draggable
                  bounds="parent"
                  onStop={(_, { y }) => setSecondTitleYAxisPosition(y)}
                >
                  <div
                    className={`${classes?.titleContainer} ${classes?.middleTitlePosition}`}
                    style={secondTitleContainerStyles}
                  >
                    <p
                      className={`maxLine1`}
                      style={{
                        color: secondTitle?.color,
                        ...boldAndItalicStyles(
                          secondTitle?.fontStyle,
                          secondTitle?.fontWeight
                        ),
                      }}
                    >
                      {secondTitle?.text}
                    </p>
                  </div>
                </Draggable>
              )}
              {/* Description */}
              {description?.isVisible && (
                <Draggable bounds="parent">
                  <p
                    className={`${classes?.descriptionText} maxLine5`}
                    style={{
                      color: description?.color,
                      ...boldAndItalicStyles(
                        description?.fontStyle,
                        description?.fontWeight
                      ),
                    }}
                  >
                    {description?.text}
                  </p>
                </Draggable>
              )}
              {/* Counter Strip */}
              {bottomStrip?.isVisible && (
                <Draggable bounds="parent">
                  <div
                    className={classes?.bottomStripContainer}
                    style={{
                      background: bottomStrip?.bgColor,
                      border: `1px solid ${bottomStrip?.outline}`,
                    }}
                  >
                    <span
                      className={`maxLine1`}
                      style={{
                        color: bottomStrip?.color,
                        ...boldAndItalicStyles(
                          bottomStrip?.fontStyle,
                          bottomStrip?.fontWeight
                        ),
                      }}
                    >
                      {bottomStrip?.text}
                    </span>
                  </div>
                </Draggable>
              )}
              {/* Bottom Title */}
              {bottomTitle?.isVisible && (
                <Draggable
                  bounds="parent"
                  onStop={(_, { y }) => setBottomTitleYAxisPosition(y)}
                >
                  <div
                    className={`${classes?.titleContainer} ${classes?.bottomTitlePosition}`}
                    style={bottomTitleContainerStyles}
                  >
                    <p
                      className={`maxLine1`}
                      style={{
                        color: bottomTitle?.color,
                        ...boldAndItalicStyles(
                          bottomTitle?.fontStyle,
                          bottomTitle?.fontWeight
                        ),
                      }}
                    >
                      {bottomTitle?.text}
                    </p>
                  </div>
                </Draggable>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* )} */}
      {/* Card BackSide */}
      {/* {!isFrontView && ( */}
      <div
        ref={backImageRef}
        style={{
          ...cardDimentionsAtScaleX(2),
          height: generatingPreview ? "1080px" : "calc(100vh - 76px)",
        }}
        // className={[classes?.dflex, isFrontView && classes?.hideContainer].join(" ")}

        className={`${classes?.dflex} ${
          isFrontView && !generatingPreview && classes?.hideContainer
        }`}
      >
        <div
          className={`${classes?.container}`}
          style={{ scale: generatingPreview ? "4" : "2" }}
        >
          {cardBacksideImg?.type?.split("/")[1] == "svg+xml" ||
          (!cardBacksideImg?.type &&
            cardBacksideImg?.split(".")[2] == "svg") ? (
            <ReactSVG
              src={
                typeof cardBacksideImg == "string"
                  ? cardBacksideImg
                  : cardBacksideImg
              }
              className={classes?.mainBgImg}
              draggable={false}
              style={{
                borderRadius: "5px",
              }}
            />
          ) : (
            <img
              src={cardBacksideImg}
              className={classes?.mainBgImg}
              style={{
                borderRadius: "5px",
              }}
            />
          )}
        </div>
      </div>
      {/* )} */}
      {/* {!btnShow && (
        <div className={classes.downloadBtn}>
          <button onClick={handleClick}>Download</button>
        </div>
      )} */}
    </div>
  );
};

export default Card2;

const mainTitleYAxisBounds = [0, 208];
const secondTitleYAxisBounds = [-97, 111];
const BottomTitleYAxisBounds = [-208, 0];
